import { useModel } from '@umijs/max';
import { useCallback, useState } from 'react';
import type { MerchantInfo } from '@/models/merchant';
import { clearEnteredMerchantId } from '@/models/merchantWorkspace';
import { getUserMerchants } from '@/services/business/user';
import { message } from '@/utils/antdStatic';

export interface ActingAsMerchantOwner {
  userId: number;
  username: string;
  displayName?: string;
  email: string;
}

export const ACTING_AS_STORAGE_KEY = 'aimerpay_acting_as_merchant_owner';
const STORAGE_KEY = ACTING_AS_STORAGE_KEY;
const MERCHANTS_CACHE_KEY = 'aimerpay_acting_as_merchants_cache';

function readFromStorage(): ActingAsMerchantOwner | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeToStorage(owner: ActingAsMerchantOwner | null) {
  if (owner) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(owner));
  } else {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(MERCHANTS_CACHE_KEY);
  }
}

function readMerchantsCache(): MerchantInfo[] {
  try {
    const raw = sessionStorage.getItem(MERCHANTS_CACHE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeMerchantsCache(merchants: MerchantInfo[]) {
  sessionStorage.setItem(MERCHANTS_CACHE_KEY, JSON.stringify(merchants));
}

export default function useActingAsMerchantModel(): {
  actingAsMerchantOwner: ActingAsMerchantOwner | null;
  enterMerchantOwnerMode: (owner: ActingAsMerchantOwner) => Promise<void>;
  exitMerchantOwnerMode: () => void;
  getMerchantsCache: () => MerchantInfo[];
} {
  const [actingAsMerchantOwner, setActingAsMerchantOwner] =
    useState<ActingAsMerchantOwner | null>(readFromStorage);
  const merchantModel = useModel('merchant');
  const { initialState } = useModel('@@initialState');

  const enterMerchantOwnerMode = useCallback(
    async (owner: ActingAsMerchantOwner): Promise<void> => {
      writeToStorage(owner);
      setActingAsMerchantOwner(owner);

      try {
        // Fetch this MerchantOwner's partners
        const response = await getUserMerchants(owner.userId);
        const partners = response?.data || [];

        const merchantList: MerchantInfo[] = partners.map(
          (p: BusinessAPI.Partner) => ({
            id: p.id,
            partnerName: p.partnerName,
            legalName: p.legalName,
            type: p.type,
            status: p.status,
            email: p.email,
            phone: p.phone,
            hasAdyenAccountHolder: p.hasAdyenAccountHolder,
            balanceAccountCount: p.balanceAccountCount,
          }),
        );

        // Seed the sessionStorage cache (survives the page reload below) and the
        // merchant model unconditionally — empty list included. These two calls
        // are what give the model a userId (currentUserIdRef) and create the
        // localStorage list key that access.ts and app.tsx's no-merchant guard
        // read. Skipping them for an owner with no merchants yet left no storage
        // at all, so creating their first merchant still looked like "no
        // merchants" and redirected into a route this mode forbids → 403.
        writeMerchantsCache(merchantList);

        // Pass SuperAdmin's own userId so localStorage keys match access.ts checks
        const currentUserId = String(
          initialState?.currentUser?.id ||
            initialState?.currentUser?.userid ||
            '',
        );
        merchantModel.loadMerchants(
          merchantList,
          currentUserId,
          merchantList[0]?.id,
        );

        // Always land un-entered, whatever the merchant count: the list is the
        // only place with the "create merchant" button, so auto-entering a
        // single-merchant owner left no way to create a second one. Clearing is
        // required now that nothing pre-seeds this key — a leftover id from a
        // previously entered owner would otherwise satisfy the workspace guard
        // with a merchant that doesn't belong to this owner.
        clearEnteredMerchantId(currentUserId);
      } catch (_error) {
        // Roll back on failure
        writeToStorage(null);
        setActingAsMerchantOwner(null);
        message.error('Failed to load merchant data');
        return;
      }

      // Use full page reload to ensure access flags are recalculated
      // from scratch. history.push + flushSync/setInitialState can fail
      // because UmiJS access plugin may cache the result and not
      // re-evaluate before the route renders → 403.
      window.location.href = '/merchants/list';
    },
    [merchantModel, initialState?.currentUser],
  );

  const exitMerchantOwnerMode = useCallback((): void => {
    writeToStorage(null);
    setActingAsMerchantOwner(null);
    merchantModel.clearMerchant();
    // Clear impersonation sessionStorage
    sessionStorage.removeItem('aimerpay_acting_as_merchant_owner');
    sessionStorage.removeItem('aimerpay_acting_as_merchants_cache');
    const currentUserId = String(
      initialState?.currentUser?.id || initialState?.currentUser?.userid || '',
    );
    clearEnteredMerchantId(currentUserId);
    // Role-aware exit target: SuperAdmin returns to the admin owner list;
    // Admin returns to its owner browse list at /merchants.
    const role =
      initialState?.currentUser?.role || initialState?.currentUser?.access;
    window.location.href =
      role === 'Admin' ? '/merchants' : '/admin/merchant-owners';
  }, [merchantModel, initialState?.currentUser]);

  return {
    actingAsMerchantOwner,
    enterMerchantOwnerMode,
    exitMerchantOwnerMode,
    getMerchantsCache: readMerchantsCache,
  };
}

export function getActingAsMerchantOwner(): ActingAsMerchantOwner | null {
  return readFromStorage();
}

export function getActingAsMerchantsCache(): MerchantInfo[] {
  return readMerchantsCache();
}

/**
 * Append a newly created merchant to the acting-as cache.
 *
 * The cache is what MerchantInitializer restores from after a page reload, so
 * without this a merchant created inside acting-as mode disappears on refresh
 * (the model would come back empty and the page would fall back to the empty
 * state). Idempotent by id.
 */
export function appendActingAsMerchantsCache(merchant: MerchantInfo): void {
  const cached = readMerchantsCache();
  if (cached.some((m) => m.id === merchant.id)) return;
  writeMerchantsCache([...cached, merchant]);
}

// Legacy alias for backward compatibility
export const getActingAsMerchant = getActingAsMerchantOwner;
